import { GeneralApiService } from '@api_patient/general_api.service';
import { AnalyticService } from '@application/analytic/analytic.service';
import { HostingService } from '@application/hosting.service';
import { DateService } from '@application/time/date.service';
import { DialogAlertService } from '@component/dialog_alert/dialog_alert.service';
import { RouterService } from '@cortex-shim/router.service';
import { CaseCommunicator } from '@feature/cases/_case_communicator';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import { Axon, SocketController } from '@visiba/axon';
import { Injectable, Injector } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { Presentation } from '@visiba/presentation';
import { ChatCommunicator, ChatCommunicatorMetadata } from './chat_communicator';
import type { ChatCommunicatorProtocol } from './chat_communicator_protocol';

interface ChatPresentation {
	communicator: ChatCommunicator | null;
	isReadyForCommunication: boolean;
}

@Injectable()
export class ChatController {
	public readonly presentation = Presentation.create<ChatPresentation>({
		communicator: null,
		isReadyForCommunication: false,
	});
	private socket: Axon | null = null;
	private connectedTo: string | null = null;
	private readonly logger = new Logger('ChatController');

	constructor(
		private readonly hostingService: HostingService,
		private readonly dateService: DateService,
		private readonly analyticService: AnalyticService,
		private readonly injector: Injector,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly routerService: RouterService,
		private readonly dialogAlertService: DialogAlertService,
		private readonly caseCommunicator: CaseCommunicator,
		private readonly generalService: GeneralApiService,
	) {
		// Empty
	}

	public join(connectId: string, metaData: ChatCommunicatorMetadata, communicatorProtocol: ChatCommunicatorProtocol): void {
		if (this.connectedTo === connectId) {
			this.logger.logInformation('tried to join an already joined room');

			return;
		}

		this.connectedTo = connectId;

		let controller: SocketController;
		if (communicatorProtocol.controllerInstance == null) {
			controller = this.createSocket().getController('messaging');
		} else {
			controller = communicatorProtocol.controllerInstance;
			this.presentation.write({
				isReadyForCommunication: true,
			});
		}

		const { communicator: currentCommunicator } = this.presentation.read();
		currentCommunicator?.destroy();

		const communicator = new ChatCommunicator(
			communicatorProtocol,
			metaData.issuesTrackedById ?? null,
			connectId,
			metaData,
			this.dateService,
			controller,
			this.analyticService,
			this.injector,
			this.unhandledIssuesService,
			this.routerService,
			this.dialogAlertService,
			this.caseCommunicator,
		);

		this.presentation.write({
			communicator,
		});
	}

	public removeCurrentBridge(): void {
		const { communicator: currentBridge } = this.presentation.read();
		currentBridge?.destroy();
		this.connectedTo = null;
	}

	public terminate(): void {
		this.removeCurrentBridge();

		this.socket
			?.close()
			.then(() => {
				this.presentation.write({
					isReadyForCommunication: false,
				});
			})
			.catch(() => {
				// noop
			});

		this.socket = null;
	}

	public sendMessage(message: string | null, fileTickets: string[]): void {
		const { communicator: currentBridge } = this.presentation.read();
		if (currentBridge == null) return;

		currentBridge.sendMessage(message, fileTickets);
		// To avoid session timeout due to inactivity, we touch the session on every sent message.
		this.generalService.touchSession();
	}

	private createSocket(): Axon {
		if (this.socket == null) {
			this.socket = new Axon(this.hostingService.wsBaseUri, (message: string) => {
				this.logger.logVerbose('{message}', message);
			});
			this.socket.events.on('onReconnecting', () => {
				this.presentation.write({
					isReadyForCommunication: false,
				});
			});
			this.socket.events.on('onReconnected', () => {
				const bridge = this.presentation.read().communicator;
				bridge?.getAllMessages();

				this.presentation.write({
					isReadyForCommunication: true,
				});
			});

			// The creation of microtask is intended to return to higher priority work.
			queueMicrotask(() => {
				this.socket
					?.open()
					.then(() => {
						this.presentation.write({
							isReadyForCommunication: true,
						});
					})
					.catch(() => {
						this.socket = null;
						this.connectedTo = null;
					});
			});
		} else {
			this.presentation.write({
				isReadyForCommunication: true,
			});
		}

		return this.socket;
	}
}
